import type {
    ContractFormType,
    ContractingCopyStage,
    ContractingFailureAlertInfo,
    ContractingStage,
    ContractingStatusFailure,
    ContractingStatusInfo,
    StateTree,
    Status
} from '@makemydeal/dr-dash-types';
import { ContractStatusBadgeInfo } from '@makemydeal/dr-dash-types';
import { createSelector } from 'reselect';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    type DealProduct,
    ENTITY_OTHER,
    CONTRACT_API_ERRORED_VPP,
    CONTRACT_API_ERRORED_VEHICLE_INSURANCE
} from '@makemydeal/dr-shared-types';

import * as configSelectors from './config';
import * as offerSelectors from './offerSelectors';
import {
    CONTRACTING_CANCEL_FAILURE_EVENT,
    CONTRACTING_OFFER_SAVE_FAILURE_EVENT,
    CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS,
    CONTRACTING_SAVED_WITH_ERRORS_EVENT,
    CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT,
    CONTRACTING_START_FAILURE_EVENT,
    CONTRACTING_TIMEOUT_FAILURE,
    CONTRACTING_VERIFY_FAILURE_EVENT
} from '../actionTypes/contractingActionTypes';

export const COPY_CREDIT_APP_FAILURE_ALERT_MESSAGE =
    "This deal requires a manual deal-type change. Please follow your store's standard cash conversion process.";
import { getCreditDecisionLender, isFlexBuyPayment } from './offerRedux';
import { isDashboardCurrentRoute } from './router';
import { getElapsedTimeInSeconds, getDurationInSeconds, getSecondsSafe } from '../utils/contractingUtils';
import { getLienholder, getTradeInAmountOwed } from './trade';

export const getState = (state: any) => state.contracting;

export const getCopyStage = (state: StateTree): ContractingCopyStage => getState(state)?.copyStage ?? 'idle';
export const getCopyAttempts = (state: StateTree): number => getState(state)?.copyAttempts ?? 0;
export const getCopyInFlightSince = (state: StateTree): number | null => getState(state)?.copyInFlightSince ?? null;
export const getLastStageTimestampMs = (state: StateTree): number | undefined => getState(state)?.lastStageTimestampMs;

export const computeStageDurationMs = (state: StateTree): number | null => {
    const lastStageTimestampMs = getLastStageTimestampMs(state);
    return lastStageTimestampMs != null ? Date.now() - lastStageTimestampMs : null;
};

export const getStatus = (state: StateTree): Status => getState(state).status;

export const getFailureCurrentState = (state: StateTree): Status => getState(state).failureCurrentState;

export const getFailureEvent = (state: StateTree): string | undefined => getState(state).failureEvent;

export const getSuccessEvent = (state: StateTree): Status => getState(state).successEvent;

export const getFailureReasons = (state: StateTree): string[] | undefined => getState(state).failureReasons;

export const getEventName = (state: StateTree): string | undefined => getState(state).eventName;

export const getEventPayload = (state: StateTree): any => getState(state).eventPayload;

export const isGenerateContractingUiOptionEnabled = (state: StateTree): boolean => {
    const isDashboard = isDashboardCurrentRoute(state);
    if (!isDashboard) {
        return false;
    }

    if (!offerSelectors.getDealRefId(state)) {
        return false;
    }

    if (isFlexBuyPayment(state)) {
        return false;
    }

    return true;
};

export const isContractingModalOpen = (state: StateTree): boolean => state.contracting.modalOpen;

export const isContractingInProgress = (state: StateTree): boolean => state.contracting.isInProgress;

export const getAlertTitle = (state: StateTree, event: string | undefined): string => {
    switch (event) {
        case CONTRACTING_CANCEL_FAILURE_EVENT:
            return 'Contract Generation Failed';
        case CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Service Unavailable';
            }
            return 'Contract Generation Failed';
        }
        case CONTRACTING_START_FAILURE_EVENT:
            return 'Unable to Generate Contract';
        case CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Service Unavailable';
            }
            return 'Unable to Generate Contract';
        }
        case CONTRACTING_SAVED_WITH_ERRORS_EVENT:
        case CONTRACTING_VERIFY_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Unable to Verify Contract: Error Details Unavailable';
            }
            return 'Unable to Verify Contract';
        }
        case CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT: {
            const failureMessages = getFailureReasons(state);
            if (!failureMessages?.length) {
                return 'Service Unavailable';
            }
            return 'Unable to Verify Contract';
        }
        case CONTRACTING_OFFER_SAVE_FAILURE_EVENT:
            return 'Service Unavailable';
        case CONTRACTING_TIMEOUT_FAILURE: {
            return 'Timeout Error';
        }
        case CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT:
        case CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS:
            return 'Unable to Generate Contract';
        default:
            return '';
    }
};

export const getAlertMessage = (state: StateTree, event: string | undefined): string => {
    switch (event) {
        case CONTRACTING_CANCEL_FAILURE_EVENT: {
            let message = 'A contract for this deal cannot be generated because it is already ';
            const currentState = getFailureCurrentState(state);
            message += currentState ? `${currentState.toLowerCase()}.` : '.';
            return message;
        }
        case CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (failureReasons?.length) {
                return 'A contract for this deal cannot be generated at this time. Please address the following issues before proceeding or ';
            }
            return 'Please try again. If the issue persists, please submit a ';
        }
        case CONTRACTING_START_FAILURE_EVENT:
            return 'The contract cannot be generated at this time. Please address the following issues before proceeding:';
        case CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (failureReasons?.length) {
                return 'The contract cannot be generated at this time. Please address the following issues before proceeding or ';
            }
            return 'Please try again. If the issue persists, please submit a ';
        }
        case CONTRACTING_SAVED_WITH_ERRORS_EVENT:
        case CONTRACTING_VERIFY_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'We encountered one or more errors that cannot be displayed here. Please click here to ';
            }
            return 'The contract failed Lender Verification. Please address the following issues before proceeding or ';
        }
        case CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT: {
            const failureMessages = getFailureReasons(state);
            if (failureMessages?.length) {
                return 'The contract failed Lender Verification. Please address the following issues before proceeding or ';
            }
            return '';
        }
        case CONTRACTING_OFFER_SAVE_FAILURE_EVENT:
            return '';
        case CONTRACTING_TIMEOUT_FAILURE: {
            if (getContractingStage(state) === 'verify') {
                return 'Generate Contract timed out during Lender Verification.';
            }

            return 'Generate Contract timed out. Please try again later.';
        }
        case CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT:
            return COPY_CREDIT_APP_FAILURE_ALERT_MESSAGE;
        case CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS:
            return `This deal doesn't have an approved lender decision, and not all lenders support spot contracts. Submit the credit application and get a lender decision to continue.`;
        default:
            return '';
    }
};

export const getDeepLinkTexts = (state: StateTree, event: string | undefined): string => {
    switch (event) {
        case CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Support Ticket.';
            }
            return 'contact support.';
        }
        case CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Support Ticket.';
            }
            return 'contact support.';
        }
        case CONTRACTING_SAVED_WITH_ERRORS_EVENT:
        case CONTRACTING_VERIFY_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'view the contract verification error details on Dealertrack.';
            }
            return 'View Contract on Dealertrack:';
        }
        case CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT: {
            const failureReasons = getFailureReasons(state);
            if (!failureReasons?.length) {
                return 'Please contact support.';
            }
            return 'contact support.';
        }
        case CONTRACTING_OFFER_SAVE_FAILURE_EVENT:
            return 'Please contact support.';
        case CONTRACTING_TIMEOUT_FAILURE: {
            if (getContractingStage(state) === 'verify') {
                return 'View Contract on Dealertrack.';
            }

            return '';
        }
        case CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS:
            return 'Attach a credit application';
        default:
            return '';
    }
};

export const getDeepLinkUrl = (state: StateTree, event: string | undefined): string => {
    switch (event) {
        case CONTRACTING_SAVED_WITH_ERRORS_EVENT:
        case CONTRACTING_VERIFY_FAILURE_EVENT:
        case CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT: {
            const dealRefId = offerSelectors.getDealRefId(state);
            return configSelectors.buildDealertrackUrlWithDealRefId(state, dealRefId);
        }
        case CONTRACTING_TIMEOUT_FAILURE: {
            if (getContractingStage(state) === 'verify') {
                const dealRefId = offerSelectors.getDealRefId(state);
                return configSelectors.buildDealertrackUrlWithDealRefId(state, dealRefId);
            }

            return '';
        }
        case CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT:
        case CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT:
        case CONTRACTING_OFFER_SAVE_FAILURE_EVENT:
            return 'https://ww2.dealertrack.app.coxautoinc.com/contactus/';
        default:
            return '';
    }
};

export const getFailureAlertInfo = (state: StateTree): ContractingFailureAlertInfo => {
    const failureEvent = getFailureEvent(state);
    const deepLinkTexts = getDeepLinkTexts(state, failureEvent);
    const message = getAlertMessage(state, failureEvent);
    const reasons = getFailureReasons(state);
    const status = getStatus(state);
    const title = getAlertTitle(state, failureEvent);
    const deepLinkUrl = getDeepLinkUrl(state, failureEvent);
    const eventName = getEventName(state);
    const eventPayload = getEventPayload(state);
    const correlationId = getContractingCorrelationId(state);

    return {
        deepLinkTexts,
        deepLinkUrl,
        message,
        reasons,
        status,
        title,
        eventName,
        eventPayload,
        correlationId
    };
};

export const getContractingStage = (state: StateTree): ContractingStage => state.contracting.stage;

export const isInProgress = (state: StateTree): boolean => state.contracting.isInProgress;

export const getContractingCorrelationId = (state: StateTree): string => state.contracting.correlationId;

export const getContractFormType = (state: StateTree): ContractFormType | undefined => state.contracting.formContractType;

export const getStartTimeMs = (state: StateTree): number | undefined => state.contracting.startTimeMs;

const calculateStageDurations = (state: StateTree, stage: string) => {
    const contractingState = getState(state);
    const requestStartTime = contractingState[`${stage}RequestStartTimeMs`];
    const requestEndTime = contractingState[`${stage}RequestEndTimeMs`];
    const eventReceivedTime = contractingState[`${stage}EventReceivedTimeMs`];
    const eventBrokerDelay = contractingState[`${stage}EventToEventBrokerDelayMs`];
    const eventBrokerReceivedTime = contractingState[`${stage}EventBrokerReceivedTimeMs`];
    const eventTime = contractingState[`${stage}EventTimeMs`];
    const eventTimeStamp = contractingState[`${stage}EventTimeStamp`];

    return {
        // from request start to event received
        [`${stage}DurationSeconds`]: getDurationInSeconds(requestStartTime, eventReceivedTime),
        // from request start to request end
        [`${stage}APIDurationSeconds`]: getDurationInSeconds(requestStartTime, requestEndTime),
        // from request end to event timestamp - CaaS processing time
        [`${stage}APICompleteToEventSeconds`]: getDurationInSeconds(requestEndTime, eventTime),
        // from request end to event received
        [`${stage}APICompleteToEventReceivedSeconds`]: getDurationInSeconds(requestEndTime, eventReceivedTime),
        // from event timestamp to event broker received duration
        [`${stage}EventToEventBrokerDelaySeconds`]: getSecondsSafe(eventBrokerDelay),
        // from event broker to the UI
        [`${stage}EventBrokerToUIDelaySeconds`]: getDurationInSeconds(eventBrokerReceivedTime, eventReceivedTime),

        // the event's timestamp
        [`${stage}EventTimeStamp`]: eventTimeStamp,
        // individual time in milliseconds since epoch
        [`${stage}RequestEndTimeMs`]: requestEndTime,
        [`${stage}EventBrokerReceivedTimeMs`]: eventBrokerReceivedTime,
        [`${stage}UIReceivedTimeMs`]: eventReceivedTime
    };
};

export const getContractFieldErrors = (state: StateTree) => state?.contracting?.fieldValidationErrors ?? [];

// Backward-compatible alias (widely used across libs/tests).
export const getContractFieldValidationErrors = getContractFieldErrors;
export const getContractErrorFlags = (state: StateTree) => state?.contracting?.fieldValidationErrorFlags ?? {};

export const getContractErrorsForEntity = (entityName: string) =>
    createSelector([getContractFieldErrors], (errors) => {
        if (!errors) return {};
        const direct = (errors as any)[entityName];
        /* istanbul ignore next */
        const otherNested = (errors as any)?.other?.[entityName];
        // Direct match has priority over nested 'other'
        return direct ?? otherNested ?? {};
    });

export const getContractErrorFieldsForEntity = (entity: string): ((state: StateTree) => string[]) =>
    createSelector([getContractErrorsForEntity(entity)], (errors): string[] => {
        return errors ? Object.keys(errors) : [];
    });

// Backward-compatible alias (widely used across libs/tests).
export const getContractValidationEntityErrorFields = getContractErrorFieldsForEntity;

const flattenErrors = (node: any, visitor: (message: string, path: string[]) => void, path: string[] = []): void => {
    if (node == null) return;
    if (typeof node === 'string') {
        visitor(node, path);
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child, index) => flattenErrors(child, visitor, [...path, String(index)]));
        return;
    }
    if (typeof node === 'object') {
        Object.entries(node).forEach(([key, child]) => flattenErrors(child, visitor, [...path, key]));
    }
};

export const getContractErrorListByEntity = createSelector([getContractFieldErrors], (errorMap): Record<string, string[]> => {
    const flatErrorsByEntity: Record<string, string[]> = {};

    flattenErrors(errorMap, (message, path) => {
        const entity = path[0] || 'unknown';
        if (!flatErrorsByEntity[entity]) {
            flatErrorsByEntity[entity] = [];
        }
        flatErrorsByEntity[entity].push(message);
    });

    return flatErrorsByEntity;
});

const hasSelectedManualVppWithoutProviderName = (state: StateTree): boolean =>
    (state.vehicleProtectionV2?.products ?? []).some(
        (product) => product.isManualProduct && product.selected && !product.productProviderName?.trim()
    );

const VPP_API_FIELDS = [
    { path: 'productCoverageMiles', flag: 'hasMilesError' },
    { path: 'productCoverageLength', flag: 'hasTermsError' },
    { path: 'productProviderName', flag: 'hasProviderNameError' },
    { path: 'productProviderAddress.city', flag: 'hasCityError' },
    { path: 'productProviderAddress.postalCode', flag: 'hasZipError' },
    { path: 'productProviderAddress.stateProvince', flag: 'hasStateError' },
    { path: 'productProviderAddress.addressLine1', flag: 'hasStreetAddressError' }
] as const;

const MISSING_REQUIRED_FIELD_MESSAGE = /(field required|is required|missing required)/i;

const isMissingRequiredVppFieldReason = (path: RegExp) => (reason: string) =>
    path.test(reason) && MISSING_REQUIRED_FIELD_MESSAGE.test(reason);

const VPP_FIELD_CHECKERS = VPP_API_FIELDS.map(({ path, flag }) => ({
    flag,
    check: isMissingRequiredVppFieldReason(new RegExp(`products\\[\\?\\]\\.${path.replace(/\./g, '\\.')}\\b`))
}));

/**
 * Groups API failure reasons by entity (matching the shape of `getContractErrorListByEntity`); unmatched reasons
 * fall under `other`. Add an entry to `entityMatchers` to route additional reason shapes into a groupable entity.
 */
export const getContractApiErrorListByEntity = createSelector(
    [
        (state: StateTree) => (getState(state) ? getFailureAlertInfo(state).reasons : undefined),
        (state: StateTree) => getFirstContractApiErroredVPP(state) !== undefined,
        (state: StateTree) => hasSelectedManualVppWithoutProviderName(state)
    ],
    (reasons, hasFlaggedVpp, hasManualVppWithoutProviderName): Record<string, string[]> => {
        const isProviderNameUnactionable = !hasFlaggedVpp && hasManualVppWithoutProviderName;
        const entityMatchers: Array<{ entity: string; test: (reason: string) => boolean }> = [
            {
                entity: CONTRACT_API_ERRORED_VPP,
                test: (reason) =>
                    VPP_FIELD_CHECKERS.some(
                        ({ flag, check }) => check(reason) && !(isProviderNameUnactionable && flag === 'hasProviderNameError')
                    )
            },
            {
                entity: CONTRACT_API_ERRORED_VEHICLE_INSURANCE,
                test: (reason) =>
                    /\bins\.(insuranceCompany|agentPhoneNumber|policyNumber|policyStartDate|policyExpirationDate)\b/.test(reason)
            }
        ];

        return (reasons ?? []).reduce<Record<string, string[]>>((grouped, reason) => {
            const matcher = entityMatchers.find(({ test }) => test(reason));
            const entity = matcher?.entity ?? ENTITY_OTHER;
            (grouped[entity] = grouped[entity] || []).push(reason);
            return grouped;
        }, {});
    }
);

export type ContractApiErroredVppFieldFlags = {
    hasMilesError: boolean;
    hasTermsError: boolean;
    hasProviderNameError: boolean;
    hasCityError: boolean;
    hasZipError: boolean;
    hasStateError: boolean;
    hasStreetAddressError: boolean;
};

export const getContractApiErroredVppFieldFlags = createSelector(
    [(state: StateTree) => (getState(state) ? getFailureAlertInfo(state).reasons : undefined)],
    (reasons): ContractApiErroredVppFieldFlags => {
        const reasonList = reasons ?? [];
        return Object.fromEntries(
            VPP_FIELD_CHECKERS.map(({ flag, check }) => [flag, reasonList.some(check)])
        ) as ContractApiErroredVppFieldFlags;
    }
);

const API_ERROR_FIELD_VALID_SENTINEL = 'valid';

// A valid field reports the sentinel (never blank) so the background validator's REQUIRED rule
// only fires for the field(s) the contract API actually flagged.
const resolveApiFieldValue = (hasError: boolean, value: string | undefined): string =>
    hasError ? value ?? '' : API_ERROR_FIELD_VALID_SENTINEL;

export type ContractApiErroredVehicleInsurance = {
    apiInsuranceCompany: string;
    apiAgentPhoneNumber: string;
    apiPolicyNumber: string;
    apiPolicyStartDate: string;
    apiPolicyExpirationDate: string;
    hasApiInsuranceCompanyError: boolean;
    hasApiAgentPhoneNumberError: boolean;
    hasApiPolicyNumberError: boolean;
    hasApiPolicyStartDateError: boolean;
    hasApiPolicyExpirationDateError: boolean;
};

export const getContractApiErroredVehicleInsurance = createSelector(
    [
        (state: StateTree) => state.baseDeal?.vehicleInsuranceInfo || null,
        (state: StateTree) => (getState(state) ? getFailureReasons(state) : undefined)
    ],
    (vehicleInsuranceInfo, reasons): ContractApiErroredVehicleInsurance | undefined => {
        const reasonList = reasons ?? [];
        const hasError = (field: string) => reasonList.some((reason) => new RegExp(`\\bins\\.${field}\\b`).test(reason));

        const hasInsuranceCompanyError = hasError('insuranceCompany');
        const hasAgentPhoneNumberError = hasError('agentPhoneNumber');
        const hasPolicyNumberError = hasError('policyNumber');
        const hasPolicyStartDateError = hasError('policyStartDate');
        const hasPolicyExpirationDateError = hasError('policyExpirationDate');

        if (
            !hasInsuranceCompanyError &&
            !hasAgentPhoneNumberError &&
            !hasPolicyNumberError &&
            !hasPolicyStartDateError &&
            !hasPolicyExpirationDateError
        ) {
            return undefined;
        }

        return {
            apiInsuranceCompany: resolveApiFieldValue(hasInsuranceCompanyError, vehicleInsuranceInfo?.insuranceCompany),
            apiAgentPhoneNumber: resolveApiFieldValue(hasAgentPhoneNumberError, vehicleInsuranceInfo?.agentPhoneNumber),
            apiPolicyStartDate: resolveApiFieldValue(hasPolicyStartDateError, vehicleInsuranceInfo?.policyStartDate),
            apiPolicyExpirationDate: resolveApiFieldValue(hasPolicyExpirationDateError, vehicleInsuranceInfo?.policyExpirationDate),
            apiPolicyNumber: resolveApiFieldValue(hasPolicyNumberError, vehicleInsuranceInfo?.policyNumber),
            hasApiInsuranceCompanyError: hasInsuranceCompanyError,
            hasApiAgentPhoneNumberError: hasAgentPhoneNumberError,
            hasApiPolicyNumberError: hasPolicyNumberError,
            hasApiPolicyStartDateError: hasPolicyStartDateError,
            hasApiPolicyExpirationDateError: hasPolicyExpirationDateError
        };
    }
);

export const getContractErrorList = createSelector([getContractFieldErrors], (errorMap): string[] => {
    const flatErrors: string[] = [];

    flattenErrors(errorMap, (message) => {
        flatErrors.push(message);
    });
    return flatErrors;
});

export const getContractHasErrors = createSelector([getContractErrorList], (errorList) => {
    return errorList.length > 0;
});

export const getIsContractingContext = createSelector(
    [
        (state: StateTree) => featureToggleSelectors.isGCDynamicValidationsEnabled(state),
        (state: StateTree) => state.contracting.fieldValidationErrors !== undefined
    ],
    (isGCDynamicValidationsEnabled, hasValidationSnapshot) => {
        return isGCDynamicValidationsEnabled && hasValidationSnapshot;
    }
);

export const getContractingDurations = (state: StateTree) => {
    const startTime = getStartTimeMs(state);
    const { saveOfferRequestStartTimeMs, saveOfferRequestEndTimeMs } = getState(state);
    const offerSaveDuration = getDurationInSeconds(saveOfferRequestStartTimeMs, saveOfferRequestEndTimeMs);

    return {
        durationSeconds: getElapsedTimeInSeconds(startTime), // overall duration
        offerSaveDuration,
        ...calculateStageDurations(state, 'cancel'),
        ...calculateStageDurations(state, 'start'),
        ...calculateStageDurations(state, 'verify')
    };
};

export const isContractCancelled = (state: StateTree): boolean => {
    return getState(state).cancelled;
};

export const isWaitingForFsResponse = (state: StateTree): boolean => state.contracting.waitingForFsResponse ?? false;

export const getFsEventDelayMs = (state: StateTree): number | undefined => state.contracting.fsEventDelayMs;

export const getSaveOfferRequestEndTimeMs = (state: StateTree): number | undefined => state.contracting.saveOfferRequestEndTimeMs;

export const getTradeInLienholderWhenOwed = createSelector([getTradeInAmountOwed, getLienholder], (amountOwed, lienHolderName) =>
    amountOwed ? { name: lienHolderName?.trim() } : undefined
);

export const getLenderProgramName = createSelector([getCreditDecisionLender], (lender) => lender?.name ?? '');

export const isContractValidationLoading = (state: StateTree): boolean => Boolean(state.contracting.contractValidationLoading);

export const getContractingStatusInfo = (state: StateTree): ContractingStatusInfo | undefined => {
    return state.contracting.contractingStatusInfo;
};

export const getContractingStatusFailure = (state: StateTree): ContractingStatusFailure | undefined => {
    return state.contracting.contractingStatusFailure;
};

export const hasContractingStatusFailure = (state: StateTree): boolean => {
    return getContractingStatusFailure(state) != null;
};

export const getIsContractGenerated = (state: StateTree): boolean => {
    const contractStatusCode = getContractingStatusInfo(state)?.contractStatusCode;
    return !!contractStatusCode && ContractStatusBadgeInfo[contractStatusCode]?.action === 'navigateToDDJ';
};

export const getFirstContractApiErroredVPP = (state: StateTree): DealProduct | undefined =>
    state?.contracting?.firstContractApiErroredVPP;
